<template>
  <div>
    <div class="auth-page">
      <div class="auth-card">
        <router-link to="/login" class="back-link">
          <i class="bi bi-arrow-left"></i>
          <span>Back to Login</span>
        </router-link>

        <!-- STEP 1: REQUEST RESET LINK -->
        <template v-if="step === 'request'">
          <div class="auth-icon">
            <i class="bi bi-key"></i>
          </div>
          <h1>Forgot your password?</h1>
          <p class="auth-subtitle">
            Enter the email linked to your account and we'll send you a link to reset your password.
          </p>

          <form @submit.prevent="submitEmail" class="auth-form">
            <label for="email">Email address</label>
            <input
              id="email"
              type="email"
              v-model="email"
              placeholder="you@example.com"
              required
              :disabled="loading"
              autocomplete="email"
            >

            <p v-if="errorMessage" class="form-error">
              <i class="bi bi-exclamation-circle"></i> {{ errorMessage }}
            </p>

            <button type="submit" class="btn-primary" :disabled="loading || cooldown > 0">
              <span v-if="loading">
                <i class="bi bi-arrow-repeat spin"></i> Sending...
              </span>
              <span v-else-if="cooldown > 0">
                Resend in {{ cooldown }}s
              </span>
              <span v-else>
                Send Reset Link
              </span>
            </button>
          </form>
        </template>

        <!-- STEP 2: CONFIRMATION -->
        <template v-else>
          <div class="auth-icon success">
            <i class="bi bi-envelope-check"></i>
          </div>
          <h1>Check your email</h1>
          <p class="auth-subtitle">
            If an account exists for <strong>{{ email }}</strong>, a password reset link is on its way.
            It'll expire in 30 minutes.
          </p>

          <div class="confirmation-actions">
            <button
              class="btn-secondary"
              @click="submitEmail"
              :disabled="loading || cooldown > 0"
            >
              <span v-if="cooldown > 0">Resend in {{ cooldown }}s</span>
              <span v-else>Resend link</span>
            </button>
            <router-link to="/login" class="btn-primary">
              Return to Login
            </router-link>
          </div>

          <p class="auth-footnote">
            Didn't get it? Check your spam folder, or make sure you entered the right email.
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";

export default {
  name: "ForgotPassword",
  data() {
    return {
      step: "request", // 'request' | 'confirmation'
      email: "",
      loading: false,
      errorMessage: "",
      cooldown: 0,
      cooldownInterval: null,
    };
  },

  beforeUnmount() {
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
  },

  methods: {
    async submitEmail() {
      this.errorMessage = "";
      this.loading = true;

      try {
        // NOTE: this endpoint doesn't exist in the controller yet —
        // see the flow notes for what it needs to do server-side.
        await apiFetch("/forgot-password", {
          method: "POST",
          body: JSON.stringify({ email: this.email }),
        });

        this.step = "confirmation";
        this.startCooldown(60);
      } catch (error) {
        console.error("Forgot password error:", error);
        // Generic message regardless of backend response —
        // avoid leaking whether the email exists.
        this.step = "confirmation";
        this.startCooldown(60);
      } finally {
        this.loading = false;
      }
    },

    startCooldown(seconds) {
      this.cooldown = seconds;
      if (this.cooldownInterval) clearInterval(this.cooldownInterval);
      this.cooldownInterval = setInterval(() => {
        this.cooldown -= 1;
        if (this.cooldown <= 0) clearInterval(this.cooldownInterval);
      }, 1000);
    },
  },
};
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--dash-bg);
  padding: 24px;
  font-family: var(--dash-font);
}

.auth-card {
  background: white;
  border-radius: var(--dash-radius-xl);
  padding: 40px;
  max-width: 440px;
  width: 100%;
  box-shadow: var(--dash-shadow);
  border: 1px solid rgba(0, 0, 0, 0.02);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--dash-muted);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 24px;
  transition: color 0.2s;
}

.back-link:hover {
  color: var(--dash-text);
}

.auth-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--dash-radius-lg);
  background: var(--dash-primary-soft);
  color: var(--dash-primary-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  margin-bottom: 20px;
}

.auth-icon.success {
  background: var(--dash-success-soft);
  color: var(--dash-success-ink);
}

h1 {
  font-size: 22px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 10px;
}

.auth-subtitle {
  font-size: 14px;
  color: var(--dash-muted);
  line-height: 1.6;
  margin: 0 0 28px;
}

.auth-subtitle strong {
  color: var(--dash-text);
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auth-form label {
  font-size: 13px;
  font-weight: 600;
  color: var(--dash-text);
}

.auth-form input {
  padding: 12px 16px;
  border-radius: var(--dash-radius);
  border: 1px solid var(--dash-line);
  background: var(--dash-surface-2);
  font-size: 14px;
  font-family: inherit;
  color: var(--dash-ink);
  transition: all 0.2s;
}

.auth-form input:focus {
  outline: none;
  border-color: var(--dash-primary);
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.auth-form input:disabled {
  opacity: 0.6;
}

.form-error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--dash-danger);
  margin: 4px 0 0;
}

.btn-primary {
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 13px 20px;
  border-radius: var(--dash-pill);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: var(--dash-shadow);
  margin-top: 12px;
  text-align: center;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-primary:hover:not(:disabled) {
  background: var(--dash-primary-hover);
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: var(--dash-bg-alt);
  color: var(--dash-text);
  border: none;
  padding: 13px 20px;
  border-radius: var(--dash-pill);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover:not(:disabled) {
  background: var(--dash-line);
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.confirmation-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.auth-footnote {
  font-size: 12px;
  color: var(--dash-faint);
  text-align: center;
  margin: 20px 0 0;
  line-height: 1.6;
}

.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>